<template>
  <div class="drawer-notes">

    <h1 class="drawer-header px-2">
      <template v-if="!dbReady">Loading notes...</template>
      <template v-else>
        Notes
        <span class="drawer-header-small">TOTAL: {{ totalItems }}</span>
      </template>
    </h1>

    <div class="edit-delete-buttons-wrapper">
      <div @click="toggleAllowEditingItemsButtons()" class="edit-txt">edit</div>
      <div @click="toggleAllowDeletingItemsButtons()" class="delete-txt">delete</div>
    </div>

    <!-- List of notes -->
    <div ref="list-of-notes" v-if="allNotes" class="list-of-entries scrollbar">

      <div
        class="list-entry flex-col px-2 py-1"
        v-for="(item, index) in allNotes"
        :key="index"
        :data-id="item.id">

        <div class="text-pink-400 flex flex-row w-full items-center justify-between">

          <div class="flex flex-row items-baseline">
            <span class="uppercase text-sm font-bold mr-1">{{ item.createdAt | createdAt2date }}</span>
            <span class="text-xs">{{ item.createdAt | createdAt2time }}</span>
          </div>

          <div class="edit-delete-icons-wrapper -mr-2">
            <!-- Delete item -->
            <svg @click="openModal(item.id)" class="delete-icon">
              <use xlink:href="#cross" />
            </svg>
            <!-- Edit item -->
            <svg @click="editOneItem(item)" class="edit-icon">
              <use xlink:href="#notes" />
            </svg>
          </div>

        </div>

        <div class="text-pink-100 font-bold uppercase mb-1">{{ item.title }}</div>
        <div class="text-gray-100 leading-tight mb-2">{{ item.text }}</div>

      </div>

    </div>

    <Pagination
      class="my-3"
      :itemsPerPage=itemsPerPage
      :totalItems=totalItems
      @onSkimmingThePages="loadNotes"
    />

    <Editor
      :setup=setup
      :mode=mode
      :toggleClass=toggleClass
      :id=id
      :title=title
      :content=content
      :createdAt=createdAt
      :feedback=feedback
      @onAddItem='addItem'
      @onUpdateItem='updateItem'
      @onResetEditor='resetMode'
    />

  </div>
</template>

<script>
import GeoMoverDB from '@/db/indexeddb'
// import Editor from '@/components/Editor'
// import Pagination from '@/components/Pagination'

export default {
  name: 'Notes',
  components: {
    Editor: () => import('@/components/Editor'),
    Pagination: () => import('@/components/Pagination')
  },
  data () {
    return {
      //
      // ----- Props for Editor
      //
      setup: 'full',
      // "mode" is used by editor to call upon its state machine
      mode: 'new', // edit, preEdit, delete
      // this class is put on the body; react to it
      toggleClass: 'notes-editor-active',
      // The item to edit / add (when infos are sent back from editor)
      id: null,
      title: null,
      content: null,
      createdAt: null,
      // The message after "save", "cancel" etc. action was (un-)successful
      feedback: null,
      //
      // ----- Props for Pagination
      //
      totalItems: 0,
      currentPage: 0,
      //
      // ----- Local attributes
      //
      // Toggle between "edit items" and "delete items". Their status defines,
      // which "mode" is sent to the Editor child component.
      allowDeletingItems: false,
      allowEditingItems: false,
      // 'id' of note to be deleted. Used in GeoMoverDB.
      idOfItemToDelete: null,
      /// reAdd original notes again
      // reAddMessage: null,
      // Override local debugging
      localDebugging: true
    }
  },
  computed: {
    dbReady: {
      // See comment in "TrackedRoutes" at same location
      get () { return this.$store.getters['settings/getDbReady'] }
    },
    // Currently this is also valid for "getPaginatedNotes"
    allNotes: {
      get () { return this.$store.getters['notes/getAllNotes'] }
    },
    itemsPerPage: {
      get () { return parseInt(this.$store.getters['settings/getMaxNotesPerPage']) }
    },
    /**
     * @see TrackedRoutes.vue
     * @requires activeMenu watcher
     */
    activeMenu: {
      get () { return this.$store.getters['ui/getActiveMenu'] }
    }
  },
  methods: {
    /**
     * ----- PAGINATION
     */
    // Called from Pagination child component
    loadNotes (page) {
      this.currentPage = page
      this.getPaginatedNotes()
    },
    /**
     * ----- EDITOR
     */
    // Called from Editor child component 'onResetEditor'
    resetMode () {
      if (this.mode !== 'delete') {
        this.mode = 'new'
      }
      /// It would be possible to deactivate the EDIT mode, if "edit" was
      /// opened right now, an item to edit was assigned and then the editor
      /// component's header was clicked in order to initiate the "toggle",
      /// which would now only close the editor and remove the item's texts
      /// from the input fields.
      /// We could close EDIT mode, too, but maybe the editor was just closed
      /// in order to look for another item to be edited. In this case keep
      /// EDIT mode active.
      /// If we do not want to keep it active, uncomment the following 3 lies.
      // if (this.mode !== 'edit') {
      // document.body.classList.remove('notes-edit')
      // }
      this.allowEditingItems = false
    },
    toggleAllowEditingItemsButtons () {
      document.body.classList.toggle('notes-edit')
      document.body.classList.remove('notes-delete')

      this.allowEditingItems = !this.allowEditingItems
      this.allowDeletingItems = false

      /// Only close the editor in case it is open (but never open it from here!)
      this.mode = !this.allowEditingItems ? 'new' : 'preEdit'
    },
    toggleAllowDeletingItemsButtons () {
      document.body.classList.toggle('notes-delete')
      document.body.classList.remove('notes-edit')

      this.allowEditingItems = false
      this.allowDeletingItems = !this.allowDeletingItems

      /// Prop for editor child component
      this.mode = !this.allowDeletingItems ? 'new' : 'delete'
    },
    /**
     * ----- "CRUD" METHODS TODO: Add visual feedbacks for the user
     */
    editOneItem (item) {
      // Update Props (sent to Editor component)
      this.mode = 'edit' // see Editor -> watch:mode
      this.id = item.id
      this.title = item.title
      this.content = item.text
      this.createdAt = item.createdAt
      this.toggleClass = 'notes-editor-active'
    },
    async addItem (obj) {
      // Construct new notes object
      const newNoteObj = {
        title: obj.title,
        text: obj.content,
        createdAt: Date.now()
      }

      // Returns true or false
      const result = GeoMoverDB.addOne('notes', newNoteObj)
      result
        .then(success => {
          this.feedback = 'Saved.'
          // Also save to VueX store's notes OR load notes again?
          // See SettingsActivityTypes for other options
          // 'new' is the "id" that invokes a visual indication on the first,
          // newly added item
          this.getPaginatedNotes('new')
          this.getTotalCountOfNotes()
        })
        .catch(error => {
          this.$l.error('Notes', 'addItem - Error saving new Note:', error)
          this.feedback = 'Error.'
        })
        .finally(() => {
          this.clearFeedbackTimeout()
          this.resetProps()
        })
    },
    async updateItem (obj) {
      // This causes trouble when id = 0, so compare against... what?? Definitely not against 0!!!
      if (obj.id === null) {
        this.feedback = 'No note selected to update.<br>'
        return false
      }

      // Rebuild the item with the edited content (title and content)
      const editedItemObj = {
        id: obj.id,
        title: obj.title,
        text: obj.content,
        createdAt: obj.createdAt || Date.now(),
        updated: Date.now()
      }

      const result = GeoMoverDB.updateOne('notes', editedItemObj, obj.id)
      result
        .then(success => {
          // this.$l.success('Notes', 'updateItem - Update successful, now handle vuex store. Success msg =', success)
          this.feedback = 'Update successful.'
          // Reload all items; the "obj.id" is sent to the pagination module in order
          // to evoke a visual feedback on the updated item
          this.getPaginatedNotes(obj.id)
          this.getTotalCountOfNotes()
        })
        .catch(error => {
          this.$l.error('Notes', 'updateItem error:', error)
          this.feedback = 'Update error.'
        })
        .finally(() => {
          this.clearFeedbackTimeout()
          this.resetProps()
        })
    },
    async deleteItem () {
      await GeoMoverDB.deleteOne('notes', this.idOfItemToDelete)
        .then(success => {
          this.getPaginatedNotes()
          this.getTotalCountOfNotes()
        })
        .catch(error => {
          this.$l.error('Notes', 'There was an error in the delete process of a note. What now? error =', error)
        })
    },
    /**
     * ----- FETCH CONTENT
     */
    async getPaginatedNotes (idOfChangedItem = null) {
      // this.$l.log('Notes: getPaginatedNotes invoked')
      // this.$l.log('> this.itemsPerPage =', this.itemsPerPage)
      // Signature: GeoMoverDB.findSelected('notes', 'createdAt', 'desc', [limit], [offset])
      const items = await GeoMoverDB.findSelection('notes', 'createdAt', 'desc', this.itemsPerPage, this.currentPage)
      this.$store.dispatch('notes/storeAllNotes', items)

      // If there was an item that was added or update, display a visual indicator
      if (idOfChangedItem !== null) {
        this.indicateChange(idOfChangedItem)
      }
    },
    // Display total number of notes next to header
    async getTotalCountOfNotes () {
      const result = await GeoMoverDB.getTotalCount('notes')
      // this.$l.log('Notes', 'getTotalCountOfNotes - result =', result)
      this.totalItems = result
    },
    /**
     * Provide a visual indication for a just recently changed item
     * @param {number|string} id - The id of the item that was changed or added
     */
    indicateChange (id = null) {
      if (id != null) {
        let changedItem = null

        if (id !== 'new') {
          changedItem = document.querySelector('[data-id="' + id + '"]')
        }

        if (id === 'new' && this.currentPage === 0) {
          // New note was added, will be the first one in list
          changedItem = this.$refs['list-of-notes'].children[0]
        }

        // Add a background color
        changedItem.classList.add('item-changed')

        // Remove the bg again after a while
        setTimeout(() => {
          changedItem.classList.remove('item-changed')
        }, 2000)
      }
    },
    /**
     * "Cleanup" methods
     */
    resetProps () {
      // these should actually reach down to the editor... but it seems they do not
      this.id = null
      this.title = null
      this.content = null
      this.createdAt = null
    },
    clearFeedbackTimeout () {
      // "this.mode" is sent to the editor component to change state to "idle"
      setTimeout(() => {
        this.mode = 'idle'
        this.feedback = null
      }, 1000)
    },
    /**
     * ----- MODAL INTERACTION
     */
    openModal (id) {
      // this.$l.log('Item to delete id =', id)
      // Intermediate saving of id of article, so in case the modal is confirmed,
      // deleting if the article can be executed more easily
      this.idOfItemToDelete = id

      // we must pass object params with all the information
      const params = {
        mode: 'confirm',
        title: 'Attention',
        text: 'Are you sure you want to delete this note?<br>This action is not undoable!',
        // we are passing callback method for our confirm button
        onConfirm: () => {
          return this.deleteItem()
        }
      }

      this.$modal.show(params)
    },
    cancelDeleteAction () {
      this.idOfItemToDelete = null
    }
  },
  mounted () {
    // this.$l.info('Notes', 'mounted', '')
    this.getPaginatedNotes()
  },
  watch: {
    /**
     * We need to observe activeMenu in order to reset the editor of a menu
     * component to its idle state.
     *
     * @example to reproduce scenario, where this watcher is needed
     * 1. open tracked routes
     * 2. click on "delete" so that is becomes active
     *    The text in the editor not reads "Deactive delete mode"
     * 3. Change to another menu, e.g. Notes
     *    This should actually reset the delete buttons of the previous menu
     *    in case they were active, and should also reset the Editor...
     * 4. Change back to tracked menu
     *    Normally the text in the editor would still read "Deactivate delete mode"
     *    The "activeMenu" watcher, however, resets the Editor state to "idle"
     *    when the oldVal matches this component.
     */
    activeMenu: function (newVal, oldVal) {
      if (oldVal === 'notes') {
        this.mode = 'new'
      }
    },
    itemsPerPage: function (newVal, oldVal) {
      // only when db is ready, the global settings are set; so
      // call db when 'itemsPerPage' ready, NOT on dbReady!
      // this.$l.log('>>> Notes:watch - itemsPerPage invoked')
      this.getPaginatedNotes()
      this.getTotalCountOfNotes()
    }
  }
}
</script>
